import * as os from 'os'
import * as crypto from 'crypto'
import { execSync } from 'child_process'
import si from 'systeminformation'

type MetaValue = string
type MetaMap = Record<string, MetaValue>

export class MachineDigest {
    public metas: MetaMap
    public secret: string
    private initialized: boolean
    private initPromise: Promise<void> | null
    private fallBackUsed: boolean = false

    constructor() {
        this.metas = {}
        this.secret = '|machine-digest|'
        this.initialized = false
        this.initPromise = null
    }

    private async _init(): Promise<void> {
        this.metas.OS_TYPE = os.type()
        this.metas.OS_PLATFORM = os.platform()
        this.metas.OS_ARCH = os.arch()
        // this.metas.OS_RELEASE = os.release()
        this.metas.OS_CPU = os.cpus().map((k) => `${k.model}`).sort().join('.')
        let _id: string | null | undefined
        try {
            if (this.metas.OS_PLATFORM == 'darwin') {
                const idMatch = /IOPlatformUUID"\s=\s"([^\s]+)"/.exec(execSync('ioreg -rd1 -c IOPlatformExpertDevice').toString('utf8'))
                if (idMatch) _id = idMatch[1]
            } else if (this.metas.OS_PLATFORM == 'linux') {
                _id = execSync('cat /var/lib/dbus/machine-id').toString('utf8')
            } else if (['win32', 'win64'].includes(this.metas.OS_PLATFORM)) {
                _id = execSync('wmic CsProduct Get UUID').toString('utf8').replace('UUID', '')
            }
        } catch (e) {}

        _id = _id?.replace('\n', '')

        const isWindows = ['win32', 'win64'].includes(this.metas.OS_PLATFORM)
        const isValidUuid = Boolean(_id && _id.includes('-'))
        if (isWindows && !isValidUuid) {
            const result = await sysInfo().catch(() => undefined)
            _id = result?.uuid && result.uuid !== '-' ? result.uuid.toUpperCase() : _id
            this.fallBackUsed = Boolean(result?.uuid && result.uuid !== '-')
        }

        this.metas.id = _id == null ? 'machine unique id' : _id.replace('\n', '')
    }

    private async _ensureInit(): Promise<void> {
        if (this.initialized) return
        if (this.initPromise) {
            await this.initPromise
            return
        }

        this.initPromise = this._init()
        await this.initPromise
        this.initialized = true
        this.initPromise = null
    }

    public async get(): Promise<{ digest: string; machine: MetaMap; fallBackUsed: boolean }> {
        await this._ensureInit()
        const digest = this._make().digest('hex')
        return { digest, machine: this.metas, fallBackUsed: this.fallBackUsed }
    }

    private _make(): crypto.Hmac {
        const ss = Object.keys(this.metas).sort().map((k) => `${k}-|-${this.metas[k]}`).join('-')
        const _raw = `M${ss}D`
        return this._hash(_raw)
    }

    private _hash(raw: crypto.BinaryLike): crypto.Hmac {
        return crypto.createHmac('sha256', this.secret).update(raw)
    }
}

async function sysInfo(): Promise<Awaited<ReturnType<typeof si.system>> | undefined> {
    return await new Promise((resolve) => {
        const timeoutId = setTimeout(() => resolve(undefined), 60_000)

        si.system().then(
            (res) => {
                clearTimeout(timeoutId)
                resolve(res)
            },
            () => {
                clearTimeout(timeoutId)
                resolve(undefined)
            }
        )
    })
}